/**
 * Skill Service
 * 
 * Provides API communication for skill taxonomy management and matching.
 */

import { apiClient } from './apiClient';

export interface SkillParameter {
  id: string;
  name: string;
  description?: string;
  parent_ref?: string;
  skill_level: number;
  baseline?: number;
  target?: number;
  unit?: string;
  is_deleted: boolean;
  deleted_at?: string;
  created_at: string;
  updated_at: string;
  parent?: {
    id: string;
    name: string;
  };
  children?: Array<{
    id: string;
    name: string;
    skill_level: number;
  }>;
}

export interface SkillParameterCreate {
  name: string;
  description?: string;
  parent_ref?: string;
  baseline?: number;
  target?: number;
  unit?: string;
}

export interface SkillParameterUpdate {
  name?: string;
  description?: string;
  parent_ref?: string;
  baseline?: number;
  target?: number;
  unit?: string;
}

export interface SkillTreeNode {
  id: string;
  name: string;
  description?: string;
  skill_level: number;
  parent_ref?: string;
  children: SkillTreeNode[];
  // Additional fields from production backend
  skill_id?: string;
  skill_name?: string;
  skill_category?: string;
  [key: string]: any; // Allow additional fields
}

export interface SkillTreeResponse {
  roots: SkillTreeNode[];
  total_count: number;
}

export interface SkillHierarchyNode {
  id: string;
  name: string;
  skill_level: number;
}

export interface SkillHierarchyResponse {
  skill: SkillParameter;
  path: SkillHierarchyNode[];
}

export interface SkillMatchRequest {
  required_skills: string[];
  artifact_type_id?: string;
  match_mode: 'any' | 'all';
}

export interface SkillMatchResult {
  artifact_id: string;
  artifact_name: string;
  artifact_type: string;
  matched_skills: string[];
  match_type: string;
}

export interface SkillMatchResponse {
  matches: SkillMatchResult[];
  total_matches: number;
  required_skills: string[];
}

// Skill Import Types
export interface SkillImportRequest {
  json_data: any;
  created_by?: string;
  overwrite_existing?: boolean;
}

export interface SkillImportResponse {
  success?: boolean;
  message?: string;
  filename?: string;
  statistics?: {
    total_processed: number;
    created: number;
    updated: number;
    skipped: number;
    errors: Array<{
      skill_name: string;
      error: string;
    }>;
  };
  imported?: number;
  updated?: number;
  errors?: Array<{
    skill_name: string;
    error: string;
  }>;
}

export const skillService = {
  /**
   * Get list of all skills
   * Supports both local backend (include_deleted, parent_id) and production backend (skill_category, skill_level, is_active, limit, offset)
   */
  getSkills: async (params?: {
    // Local backend params
    include_deleted?: boolean;
    parent_id?: string;
    // Production backend params
    skill_category?: string;
    skill_level?: number;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<SkillParameter[]> => {
    try {
      const response = await apiClient.get('/skills', { params });
      console.log('[skillService] Skills response:', response.data);
      
      // Handle different response structures
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data?.skills && Array.isArray(response.data.skills)) {
        return response.data.skills;
      } else {
        console.warn('[skillService] Unexpected skills response structure:', response.data);
        return [];
      }
    } catch (error: any) {
      console.error('[skillService] Error fetching skills:', error);
      console.error('[skillService] Error response:', error?.response?.data);
      throw error;
    }
  },

  /**
   * Get entire skill tree
   */
  getSkillTree: async (): Promise<SkillTreeResponse> => {
    try {
      const response = await apiClient.get('/skills/tree');
      console.log('[skillService] Tree response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[skillService] Error fetching skill tree:', error);
      console.error('[skillService] Error response:', error?.response?.data);
      throw error;
    }
  },

  /**
   * Get a specific skill by ID
   */
  getSkill: async (
    id: string,
    params?: {
      include_children?: boolean;
      include_parent?: boolean;
    }
  ): Promise<SkillParameter> => {
    const response = await apiClient.get(`/skills/${id}`, { params });
    return response.data;
  },

  /**
   * Create a new skill
   */
  createSkill: async (data: SkillParameterCreate): Promise<SkillParameter> => {
    const response = await apiClient.post('/skills', data);
    return response.data;
  },

  /**
   * Update an existing skill
   */
  updateSkill: async (
    id: string,
    data: SkillParameterUpdate
  ): Promise<SkillParameter> => {
    const response = await apiClient.put(`/skills/${id}`, data);
    return response.data;
  },

  /**
   * Delete a skill (soft delete)
   */
  deleteSkill: async (id: string): Promise<void> => {
    await apiClient.delete(`/skills/${id}`);
  },

  /**
   * Get hierarchy path for a skill (from root to skill)
   */
  getSkillHierarchy: async (id: string): Promise<SkillHierarchyResponse> => {
    const response = await apiClient.get(`/skills/${id}/hierarchy`);
    return response.data;
  },

  /**
   * Get children of a skill
   */
  getSkillChildren: async (
    id: string,
    recursive: boolean = false
  ): Promise<SkillParameter[]> => {
    const response = await apiClient.get(`/skills/${id}/children`, {
      params: { recursive }
    });
    return response.data;
  },

  /**
   * Find artifacts that match skill requirements
   */
  findQualifiedArtifacts: async (
    request: SkillMatchRequest
  ): Promise<SkillMatchResponse> => {
    const response = await apiClient.post('/skills/match', request);
    return response.data;
  },

  /**
   * Get skill path as a string (for display)
   */
  getSkillPathString: async (id: string): Promise<string> => {
    const hierarchy = await skillService.getSkillHierarchy(id);
    return hierarchy.path.map(node => node.name).join(' > ');
  },

  /**
   * Import skills from JSON data
   */
  importSkills: async (
    data: SkillImportRequest
  ): Promise<SkillImportResponse> => {
    const response = await apiClient.post('/skills/import', data);
    return response.data;
  },

  /**
   * Import skills from file
   */
  importSkillsFromFile: async (
    file: File,
    createdBy?: string,
    overwriteExisting: boolean = false
  ): Promise<SkillImportResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    if (createdBy) formData.append('created_by', createdBy);
    formData.append('overwrite_existing', overwriteExisting.toString());

    const response = await apiClient.post('/skills/import-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
};


